import React, { useEffect, useState } from 'react';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CreditCard, Loader2, Link2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const Row = ({ label, value, ok, testId }) => (
  <div className="flex justify-between text-sm py-1.5 border-b border-white/5">
    <span className="text-[#E8E4DE]/60">{label}</span>
    <span className={ok ? 'text-green-400' : 'text-yellow-400'} data-testid={testId}>{value}</span>
  </div>
);

export const StripeStatusCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pi, setPi] = useState('');
  const [linking, setLinking] = useState(false);

  const linkPayment = async (e) => {
    e.preventDefault();
    setLinking(true);
    try {
      const res = await authFetch(`${API_BASE}/api/invoices/reconcile-payment`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_intent_id: pi.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(Array.isArray(data.detail) ? 'Enter a Stripe payment ID that starts with pi_' : data.detail);
      toast.success(data.already_paid ? `Invoice ${data.invoice_number} was already marked paid`
        : `Invoice ${data.invoice_number} marked paid ($${((data.amount_paid_cents || 0) / 100).toFixed(2)})`, { duration: 8000 });
      setPi('');
    } catch (err) {
      toast.error("Couldn't link that payment", { description: err.message, duration: 10000 });
    } finally {
      setLinking(false);
    }
  };

  const load = () =>
    authFetch(`${API_BASE}/api/invoices/payment-status`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setStatus)
      .catch(() => setStatus(null));

  useEffect(() => { load(); }, []);

  const setupWebhook = async () => {
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/api/invoices/stripe/setup-webhook`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Webhook setup failed');
      if (data.note) toast.warning(data.note);
      else toast.success(data.status === 'created' ? 'Stripe webhook created and secured' : 'Stripe webhook already set up');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  const live = status.stripe_mode === 'live';
  return (
    <Card className="bg-[#0f1724] border-white/10" data-testid="stripe-status-card">
      <CardHeader>
        <CardTitle className="text-lg text-white flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-[#C6A14A]" /> Stripe Payments
        </CardTitle>
        <CardDescription className="text-[#E8E4DE]/60">Payment mode and connection status. Secret values are never shown.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-1">
        <Row label="Mode" value={live ? 'LIVE' : 'TEST'} ok={live} testId="stripe-mode" />
        <Row label="Secret key" value={status.secret_key_type} ok={status.secret_key_type.includes(status.stripe_mode)} testId="stripe-secret-type" />
        <Row label="Publishable key" value={status.publishable_key_type} ok={status.publishable_key_type === status.stripe_mode} testId="stripe-publishable-type" />
        <Row label="Webhook secret" value={status.webhook_secret_source} ok={status.webhook_secret_source !== 'missing'} testId="stripe-webhook-source" />
        <p className="text-xs text-[#E8E4DE]/40 pt-2 break-all" data-testid="stripe-webhook-url">Webhook: {status.webhook_url}</p>
        <form onSubmit={linkPayment} className="pt-4 space-y-2 border-t border-white/10 mt-3">
          <p className="text-sm text-white">Link a Stripe payment to its invoice</p>
          <p className="text-xs text-[#E8E4DE]/50">Paste the payment ID from Stripe (starts with pi_) if a client paid but the invoice still shows unpaid.</p>
          <div className="flex gap-2">
            <Input value={pi} onChange={(e) => setPi(e.target.value)} placeholder="pi_3U..."
              className="bg-[#050816] border-white/10 text-white placeholder:text-white/30" data-testid="reconcile-pi-input" />
            <Button type="submit" size="sm" disabled={linking || !pi.trim()}
              className="bg-[#C6A14A] text-[#050816] hover:bg-[#d4b25c]" data-testid="reconcile-pi-btn">
              {linking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4 mr-1.5" />} Link
            </Button>
          </div>
        </form>
        <div className="pt-3">
          <Button size="sm" onClick={setupWebhook} disabled={busy}
            className="bg-[#C6A14A] text-[#050816] hover:bg-[#d4b25c]" data-testid="stripe-setup-webhook-btn">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Set up / verify webhook
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
